import { InboxAiRetryButton } from "./InboxAiRetryButton";
import { InboxDeleteSelected } from "./InboxDeleteAction";
import { useInboxDialogEscape } from "./useInboxDialogEscape";
import { useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { errorMessage } from "@/lib/api";
import { companyInboxLinked, confirmCompanyInboxBatch, rejectCompanyInboxBatch } from "@/lib/companyInbox";
import { inboxPending, inboxTitle, notifyInboxChanged, type InboxBatchResponse, type InboxItem } from "@/lib/documentInbox";

type BatchPreview = { action: "confirm" | "reject"; items: InboxItem[] };
function archiveEligible(item: InboxItem) {
  return inboxPending(item) && item.owner_kind === "firma" && Boolean(item.firma_kategorija) && !companyInboxLinked(item);
}
function title(item: InboxItem) { return item.naziv || inboxTitle(item); }

export function InboxCompanyBatch({ items, selected, disabled, onComplete, onBusy, onDialogChange }: {
  items: InboxItem[];
  selected: Set<string>;
  disabled: boolean;
  onComplete: (ids: string[]) => void;
  onBusy: (busy: boolean) => void;
  onDialogChange?: (open: boolean) => void;
}) {
  const { can } = useAuth();
  const [preview, setPreview] = useState<BatchPreview | null>(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<InboxBatchResponse | null>(null);
  const running = useRef(false);
  const selectedItems = items.filter((item) => selected.has(item.id));
  const pendingCompany = selectedItems.filter((item) => item.owner_kind !== "radnik" && inboxPending(item));
  const eligible = selectedItems.length > 0 && selectedItems.every(archiveEligible);
  const hasLinked = selectedItems.some(companyInboxLinked);
  const allowed = can("company_document", "confirm");

  function open(action: BatchPreview["action"]) {
    if (!allowed || disabled || running.current || (action === "confirm" ? !eligible : !pendingCompany.length)) return;
    // Keep the exact selection reviewed by the user even if the parent refreshes.
    setPreview({ action, items: [...(action === "confirm" ? selectedItems : pendingCompany)] });
    onDialogChange?.(true);
    setReason(""); setError(null); setResult(null);
  }
  async function processBatch() {
    if (!preview || !allowed || disabled || running.current || (preview.action === "reject" && !reason.trim())) return;
    if (preview.action === "confirm" && !preview.items.every(archiveEligible)) return;
    running.current = true; setBusy(true); onBusy(true); setError(null);
    const aggregate: InboxBatchResponse = { results: [], confirmed: 0, rejected: 0, skipped: 0, errors: 0, confidence_threshold: null };
    try {
      for (let offset = 0; offset < preview.items.length; offset += 200) {
        const ids = preview.items.slice(offset, offset + 200).map((item) => item.id);
        const response = preview.action === "confirm"
          ? await confirmCompanyInboxBatch(ids)
          : await rejectCompanyInboxBatch(ids, reason.trim());
        aggregate.results.push(...response.results);
        aggregate.confirmed += response.confirmed; aggregate.rejected += response.rejected;
        aggregate.skipped += response.skipped; aggregate.errors += response.errors;
        setResult({ ...aggregate, results: [...aggregate.results] });
        const completed = response.results.filter((row) => row.outcome === "confirmed" || row.outcome === "rejected").map((row) => row.id);
        if (completed.length) { onComplete(completed); notifyInboxChanged(); }
      }
    } catch (cause: unknown) {
      setError(errorMessage(cause, "Skupna obrada nije uspjela. Osvježite ladicu prije ponavljanja."));
    } finally {
      // A failed later chunk must not leave a retry button for already completed chunks.
      setPreview(null); onDialogChange?.(false); running.current = false; setBusy(false); onBusy(false);
    }
  }

  useInboxDialogEscape(!!preview, () => { setPreview(null); onDialogChange?.(false); }, busy);
  const canRetry = can("dokument", "inbox_confirm") && items.some(item => item.import_source === "uvoz_znr_agencija");
  if (!allowed) return canRetry ? <InboxAiRetryButton items={selectedItems} disabled={disabled} onBusy={onBusy} onComplete={() => onComplete([])} /> : null;
  return <section className="avm-surface shrink-0 space-y-2 rounded border bg-background p-3" aria-label="Skupna obrada tvrtke">
    {canRetry && <InboxAiRetryButton items={selectedItems} disabled={disabled || busy || !!preview} onBusy={onBusy} onComplete={() => onComplete([])} />}
    <p>Odabrano: {selectedItems.length}</p>
    {hasLinked && <p className="text-sm">Dokumente povezane s obvezom potvrdite pojedinačno.</p>}
    {!hasLinked && selectedItems.length > 0 && !eligible && <p className="text-sm">Skupno se potvrđuju samo neobrađeni dokumenti firme s kategorijom. Ostale dokumente potvrdite pojedinačno.</p>}
    <div className="flex flex-wrap gap-2">
      <Button disabled={disabled || busy || !!preview || !eligible} onClick={() => open("confirm")}>Potvrdi odabrane u arhivu</Button>
      <InboxDeleteSelected items={items} selected={selected} disabled={disabled || busy} onComplete={onComplete} onBusy={onBusy} onDialogChange={onDialogChange} />
      <Button variant="destructive" disabled={disabled || busy || !!preview || !pendingCompany.length} onClick={() => open("reject")}>Odbaci odabrane</Button>
    </div>
    {error && <p role="alert">{error}</p>}
    {result && <div role="status" className="avm-surface max-h-48 overflow-auto rounded border p-3">
      <p>Potvrđeno: {result.confirmed} · Odbačeno: {result.rejected} · Preskočeno: {result.skipped} · Pogreške: {result.errors}</p>
      {result.results.filter((row) => row.message).map((row) => <p key={row.id}>{row.message}</p>)}
    </div>}
    {preview && <section role="dialog" aria-label="Potvrda skupne obrade tvrtke" className="space-y-3 border-t pt-3">
      <h2 className="font-semibold">{preview.action === "confirm" ? "Spremi odabrane dokumente samo u arhivu" : "Odbaci odabrane dokumente"}</h2>
      <ul className="max-h-48 overflow-auto">{preview.items.map((item) => <li key={item.id}>{title(item)}</li>)}</ul>
      {preview.action === "confirm" ? <p>Obveze se neće zatvarati. Dokumenti bez kategorije bit će preskočeni. Pravilo čuvanja treba provjeriti.</p>
        : <label data-required-label>Razlog odbacivanja *<textarea className="w-full rounded border p-2" maxLength={500} value={reason} disabled={busy} onChange={(event) => setReason(event.target.value)} /></label>}
      <div className="flex flex-wrap gap-2">
        <Button variant={preview.action === "reject" ? "destructive" : "default"} disabled={disabled || busy || (preview.action === "reject" && !reason.trim())} onClick={() => void processBatch()}>Potvrdi skupnu obradu</Button>
        <Button variant="outline" disabled={busy} onClick={() => { setPreview(null); onDialogChange?.(false); }}>Odustani</Button>
      </div>
    </section>}
  </section>;
}
